// src/app/quote-details.jsx
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import API_BASE_URL from '../apiConfig.js';

// Helper to fetch a token from localStorage
const getToken = () => {
  try {
    return localStorage.getItem('userToken');
  } catch {
    return null;
  }
};

export default function QuoteDetails() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');

  const [quote, setQuote] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedQuote, setEditedQuote] = useState(null);
  const [saving, setSaving] = useState(false);
  const [userPermissions, setUserPermissions] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [businessInfo, setBusinessInfo] = useState({}); // Add business info state
  const [businessLogoUrl, setBusinessLogoUrl] = useState(null); // Add business logo URL state
  const [customers, setCustomers] = useState([]); // Add customers state for PDF generation
  
  // Custom Modal state
  const [customModal, setCustomModal] = useState({
    open: false,
    title: '',
    message: '',
    buttons: []
  });

  const showCustomModal = (title, message, buttons) => {
    setCustomModal({ open: true, title, message, buttons });
  };

  const closeCustomModal = () => {
    setCustomModal({ open: false, title: '', message: '', buttons: [] });
  };

  // Check if user has quote write permissions
  const hasQuoteWritePermissions = () => {
    // Owners and admins can ALWAYS edit/delete quotes (check this FIRST)
    if (userPermissions?.user_type === 'owner' || userPermissions?.user_type === 'admin') {
      return true;
    }
    
    // For other users, check if they have write permission
    return userPermissions?.quote_perm === 'write';
  };

  // Load user permissions on component mount and check access
  useEffect(() => {
    const loadUserPermissions = () => {
      try {
        const permissions = localStorage.getItem('userPermissions');
        if (permissions) {
          const parsedPermissions = JSON.parse(permissions);
          setUserPermissions(parsedPermissions);
          
          const user_type = parsedPermissions?.user_type;
          const quotePerm = parsedPermissions?.quote_perm;

          // Block access if user is NOT (Owner OR Admin) AND has no invoice permission
          if ((user_type !== 'owner' && user_type !== 'admin') && quotePerm === 'none') {
            alert("You don't have permission to view quotes. Please contact your account administrator.");
            navigate('/'); // Redirect to home page
            return;
          }
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
      }
    };
    loadUserPermissions();
  }, [navigate]);

  // Check if user can see prices
  const canSeePrices = () => {
    return userPermissions?.price_perm === 'visible';
  };

  useEffect(() => {
    const loadQuote = async () => {
      try {
        const token = getToken();
        const response = await axios.get(`${API_BASE_URL}/api/quotes/id/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = response.data || {};
        const normalized = {
          ...data,
          customerName: data.customerName ?? data.customer ?? '',
        };
        setQuote(normalized);
        setEditedQuote(normalized);
        
        // Capture business info from backend response
        if (data.businessInfo) {
          setBusinessInfo(data.businessInfo);
          // Set logo preview if exists
          if (data.businessInfo?.businessLogo) {
            setBusinessLogoUrl(`${API_BASE_URL}/api/uploads/${data.businessInfo.businessLogo}`);
          }
        }
      } catch (error) {
        console.error('Error loading quote:', error);
        window.alert('Failed to load quote.');
      }
    };
    if (id) loadQuote();
  }, [id]);

  // Load customers for preview
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const token = getToken();
        const response = await axios.get(`${API_BASE_URL}/api/customers`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        const list = Array.isArray(response.data) ? response.data : response.data?.customers ?? [];
        setCustomers(list);
      } catch (error) {
        console.error('Error loading customers:', error);
      }
    };
    loadCustomers();
  }, []);

  const saveEdits = async () => {
    // Validate required fields: customer and title
    if (!editedQuote.customerName?.trim()) {
      window.alert('Customer is required. Please enter a customer name.');
      return;
    }

    if (!editedQuote.title?.trim()) {
      window.alert('Title is required. Please enter a title for the quote.');
      return;
    }

    // Handle price: if empty or invalid, default to "0.00"
    let cleanedPrice = editedQuote.price?.toString().trim() || '';
    if (cleanedPrice === '' || isNaN(parseFloat(cleanedPrice))) {
      cleanedPrice = '0.00';
    } else {
      const numPrice = parseFloat(cleanedPrice);
      cleanedPrice = numPrice.toFixed(2);
    }

    try {
      // Create payload with cleaned data
      const payload = {
        title: editedQuote.title.trim(),
        description: editedQuote.description?.trim() || '', // Save as empty string if blank
        price: parseFloat(cleanedPrice),
        customer: editedQuote.customerName?.trim() || editedQuote.customer?.trim() || '',
        customerId: editedQuote.customerId,
        notes: editedQuote.notes?.trim() || '', // Save as empty string if blank
      };

      // Skip API if no changes
      const hasChanges = Object.keys(payload).some(
        (k) => {
          const originalValue = k === 'customer' ? quote.customerName : quote[k];
          return payload[k] !== originalValue;
        }
      );
      
      if (!hasChanges) {
        setIsEditing(false);
        return;
      }

      const token = getToken();
      await axios.put(`${API_BASE_URL}/api/quotes/${id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setQuote({ ...quote, ...payload, customerName: payload.customer });
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving quote edits:', error);
      window.alert('Failed to save changes.');
    }
  };

  const deleteQuote = async () => {
    const confirm = window.confirm('Are you sure you want to delete this quote?');
    if (!confirm) return;

    try {
      const token = getToken();
      await axios.delete(`${API_BASE_URL}/api/quotes/delete/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      navigate(-1);
    } catch (error) {
      console.error('Error deleting quote:', error);
      if (error.response?.status === 401) {
        window.alert('Session expired. Please log in again.');
      } else if (error.response?.status === 404) {
        window.alert('Quote not found or not authorized to delete.');
      } else {
        window.alert('Failed to delete quote. Please try again.');
      }
    }
  };

  const emailQuotePDF = async () => {
    if (!id || sendingEmail) return;

    try {
      setSendingEmail(true);
      const token = getToken();
      const response = await axios.post(`${API_BASE_URL}/api/quotes/${id}/email`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (response.data && response.data.success) {
        showCustomModal(
          'Email Sent Successfully! ✅',
          `Quote has been emailed as PDF to customer`,
          [{ text: 'Great!', style: 'primary', onPress: closeCustomModal }]
        );
        // Reload quote to update sentEmail status
        const { data } = await axios.get(`${API_BASE_URL}/api/quotes/id/${encodeURIComponent(id)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        setQuote(prevQuote => ({
          ...prevQuote,
          sentEmail: 'yes',
          dateSent: data.dateSent || new Date().toISOString()
        }));
        setEditedQuote(prevQuote => ({
          ...prevQuote,
          sentEmail: 'yes',
          dateSent: data.dateSent || new Date().toISOString()
        }));
      } else {
        showCustomModal(
          'Email Status Unclear ⚠️',
          response.data?.message || 'Email may have been sent, but status is unclear.',
          [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
        );
      }
    } catch (emailError) {
      console.error('Error sending quote email:', emailError);
      
      let errorMessage = 'Failed to send email. Please try again.';
      
      if (emailError.response?.status === 400) {
        errorMessage = `Customer email issue. Please update customer information.`;
      } else if (emailError.response?.status === 404) {
        errorMessage = 'Quote not found.';
      } else if (emailError.response?.data?.detail) {
        errorMessage = emailError.response.data.detail;
      } else if (emailError.response?.data?.message) {
        errorMessage = emailError.response.data.message;
      } else if (emailError.message) {
        errorMessage = `Network error: ${emailError.message}`;
      }
      
      showCustomModal(
        'Email Failed ❌',
        `Failed to send email: ${errorMessage}`,
        [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
      );
    } finally {
      setSendingEmail(false);
    }
  };

  // Function to generate PDF and trigger browser download - matching invoice-details.jsx
  const generateAndSharePDF = async () => {
    try {
      const customer = customers.find(c => c.name === quote.customerName);
      
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
            .header { display: flex; justify-content: space-between; margin-bottom: 30px; border-bottom: 2px solid #2563eb; padding-bottom: 20px; }
            .logo-container { margin-bottom: 10px; min-height: 85px; }
            .business-logo { max-width: 150px; max-height: 80px; object-fit: contain; }
            .company-name { font-size: 24px; font-weight: bold; color: #2563eb; margin-bottom: 10px; }
            .company-details { font-size: 13px; color: #666; line-height: 1.6; }
            .quote-title { font-size: 28px; font-weight: bold; color: #2563eb; }
            .quote-details { font-size: 13px; color: #666; margin-top: 8px; }
            .section-title { font-size: 18px; font-weight: bold; margin: 30px 0 15px 0; padding-bottom: 5px; border-bottom: 2px solid #e5e7eb; }
            .customer-info { background: #f8fafc; padding: 20px; border-radius: 8px; border-left: 4px solid #2563eb; }
            .service-item { border: 1px solid #e5e7eb; padding: 20px; border-radius: 8px; margin: 20px 0; }
            .pricing-section { background: #f8fafc; padding: 20px; border: 2px solid #2563eb; border-radius: 8px; margin: 30px 0; }
            .price-row { display: flex; justify-content: space-between; padding: 15px 0; border-bottom: 1px solid #e5e7eb; }
            .total-row { border-top: 2px solid #2563eb; border-bottom: none; font-size: 20px; font-weight: bold; margin-top: 10px; }
            .terms-section { background: #fefce8; padding: 20px; border-radius: 8px; border-left: 4px solid #eab308; margin: 30px 0; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 2px solid #e5e7eb; text-align: center; font-size: 13px; color: #666; }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <div class="logo-container">
                ${businessLogoUrl ? `<img src="${businessLogoUrl}" alt="Business Logo" class="business-logo" />` : ''}
              </div>
              <div class="company-name">${businessInfo?.businessName || 'Your Business Name'}</div>
              <div class="company-details">
                ${businessInfo?.firstName || businessInfo?.lastName ? `${[businessInfo.firstName, businessInfo.lastName].filter(Boolean).join(' ')}<br>` : ''}
                ${businessInfo?.address || ''}<br>
                ${businessInfo?.city && businessInfo?.state ? `${businessInfo.city}, ${businessInfo.state} ${businessInfo.zip || ''}` : 'City, State'}<br>
                Phone: ${businessInfo?.phone || ''}<br>
                Email: ${businessInfo?.email || ''}
              </div>
            </div>
            <div style="text-align: right;">
              <div class="quote-title">QUOTE</div>
              <div class="quote-details">
                Quote #: Q-${quote.id || 'XXXX'}<br>
                Date: ${new Date().toLocaleDateString()}<br>
                Valid Until: ${new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString()}
              </div>
            </div>
          </div>
          
          <div class="section-title">Quote For:</div>
          <div class="customer-info">
            <strong>${quote.customerName || 'Customer Name'}</strong><br>
            ${customer?.address || ''}<br>
            ${customer?.city && customer?.state ? `${customer.city}, ${customer.state} ${customer.zip || ''}` : ''}<br>
            ${customer?.phone ? `Phone: ${customer.phone}` : ''}<br>
            ${customer?.email ? `Email: ${customer.email}` : ''}
          </div>
          
          <div class="section-title">Services</div>
          <div class="service-item">
            <strong style="font-size: 18px;">${quote.title || 'Service Title'}</strong><br><br>
            ${quote.description || 'No description provided'}
          </div>
          
          <div class="pricing-section">
            <div class="price-row">
              <span>Service Total:</span>
              <span><strong>$${parseFloat(quote.price || 0).toFixed(2)}</strong></span>
            </div>
            <div class="price-row total-row">
              <span>Total Amount:</span>
              <span>$${parseFloat(quote.price || 0).toFixed(2)}</span>
            </div>
          </div>
          
          <div class="terms-section">
            <strong>Quote Terms & Conditions</strong><br><br>
            • This quote is valid for 30 days from the date above<br>
            • Prices are subject to change without notice<br>
            • A 50% deposit may be required to begin work<br>
            • Final pricing may vary based on actual work performed<br>
            • Additional charges may apply for work outside the scope described above
          </div>
          
          <div class="footer">
            <p>Thank you for considering our services. We look forward to working with you!</p>
            <p><strong>Questions?</strong> Contact us at ${businessInfo?.phone || ''} or ${businessInfo?.email || ''}</p>
          </div>
        </body>
        </html>
      `;
      
      // Generate PDF filename
      const customerFileName = (quote.customerName || 'Customer')
        .replace(/[^a-z0-9\s]/gi, '')  // Remove special characters
        .trim()
        .replace(/\s+/g, '_');  // Replace spaces with underscores
      
      const pdfFileName = `${customerFileName}_Quote.pdf`;
      
      // Create a temporary iframe to render the HTML and trigger print
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'fixed';
      printFrame.style.right = '0';
      printFrame.style.bottom = '0';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = '0';
      document.body.appendChild(printFrame);
      
      const printDocument = printFrame.contentWindow.document;
      printDocument.open();
      printDocument.write(htmlContent);
      printDocument.close();
      
      // Set the document title for the PDF filename
      printDocument.title = pdfFileName;
      
      // Wait for content to load, then trigger print
      setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
        
        // Clean up after a delay
        setTimeout(() => {
          document.body.removeChild(printFrame);
        }, 1000);
      }, 500);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  if (!quote) return <Text>Loading...</Text>;

  return (
    <KeyboardAvoidingView
      style={{ flex: 1, padding: 20, backgroundColor: '#f0f8ff' }}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={0}
    >
      <ScrollView contentContainerStyle={{ flexGrow: 1, paddingBottom: 50 }} keyboardShouldPersistTaps="handled">
        <Text style={styles.label}>Customer:</Text>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text style={styles.value}>{quote.customerName}</Text>
          <TouchableOpacity onPress={() => navigate(`/customer-details?id=${quote.customerId}`)}>
            <Text style={styles.customerDetailsLink}>Customer Details</Text>
          </TouchableOpacity>
        </View>

        <Text style={styles.label}>Title:</Text>
        {isEditing ? (
          <TextInput
            style={styles.input}
            value={editedQuote.title}
            onChangeText={(text) => setEditedQuote({ ...editedQuote, title: text })}
          />
        ) : (
          <Text style={styles.value}>{quote.title}</Text>
        )}

        <Text style={styles.label}>Description:</Text>
        {isEditing ? (
          <TextInput
            style={[styles.input, { height: 100 }]}
            multiline
            value={editedQuote.description}
            onChangeText={(text) =>
              setEditedQuote({ ...editedQuote, description: text })
            }
          />
        ) : (
          <Text style={styles.value}>{quote.description || 'No Description'}</Text>
        )}

        {canSeePrices() && (
          <>
            <Text style={styles.label}>Price:</Text>
            {isEditing ? (
              <TextInput
                style={styles.input}
                keyboardType="numeric"
                value={editedQuote.price?.toString() || ''}
                onChangeText={(text) => {
                  // Only allow numbers and decimal point
                  const cleanedText = text.replace(/[^0-9.]/g, '');
                  setEditedQuote({ ...editedQuote, price: cleanedText });
                }}
              />
            ) : (
              <Text style={styles.value}>{`$${quote.price}`}</Text>
            )}
          </>
        )}

        <Text style={styles.label}>Notes:</Text>
        {isEditing ? (
          <TextInput
            style={[styles.input, { height: 100 }]}
            multiline
            value={editedQuote.notes ?? ''}
            onChangeText={(text) => setEditedQuote({ ...editedQuote, notes: text })}
          />
        ) : (
          <Text style={styles.value}>{quote.notes || 'No notes available'}</Text>
        )}

        {/* Sent */}
        <Text style={styles.label}>Sent:</Text>
        <View style={styles.sentContainer}>
          <View style={styles.sentStatusContainer}>
            <Text style={styles.value}>
              {quote.sentEmail === 'yes' || quote.sentText === 'yes' ? 'Yes' : 'No'}
            </Text>
            {/* Show dateSent if email was sent */}
            {quote.sentEmail === 'yes' && quote.dateSent && (
              <Text style={styles.dateSentText}>
                Emailed: {new Date(quote.dateSent).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric', 
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}
              </Text>
            )}
          </View>
          
          {/* Action buttons next to sent status */}
          <View style={styles.sentButtonsContainer}>
            {quote.sentEmail === 'yes' || quote.sentText === 'yes' ? (
              // If already sent, show Resend and View Quote buttons
              <>
                <TouchableOpacity
                  style={[styles.actionButton, styles.resendButton, sendingEmail && { opacity: 0.7 }]}
                  onPress={emailQuotePDF}
                  disabled={sendingEmail}
                >
                  <Text style={styles.actionButtonText}>
                    {sendingEmail ? 'Sending…' : 'Resend'}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.actionButton, styles.previewButton]}
                  onPress={() => setPreviewOpen(true)}
                >
                  <Text style={styles.actionButtonText}>View Quote</Text>
                </TouchableOpacity>
              </>
            ) : (
              // If not sent, show Email PDF to Customer and Preview PDF buttons
              <>
                <TouchableOpacity
                  style={[styles.actionButton, styles.emailButton, sendingEmail && { opacity: 0.7 }]}
                  onPress={emailQuotePDF}
                  disabled={sendingEmail}
                >
                  <Text style={styles.actionButtonText}>
                    {sendingEmail ? 'Sending…' : 'Email PDF to Customer'}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.actionButton, styles.previewButton]}
                  onPress={() => setPreviewOpen(true)}
                >
                  <Text style={styles.actionButtonText}>Preview PDF</Text>
                </TouchableOpacity>
              </>
            )}
          </View>
        </View>

        {isEditing ? (
          <View style={styles.buttonContainer}>
            <View style={styles.rowContainer}>
              <TouchableOpacity 
                style={[styles.button, styles.saveButton]} 
                onPress={saveEdits}
              >
                <Text style={styles.buttonText}>Save</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.button, styles.cancelButton]}
                onPress={() => {
                  setEditedQuote({ ...quote });
                  setIsEditing(false);
                }}
              >
                <Text style={styles.buttonText}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        ) : (
          hasQuoteWritePermissions() && (
            <View style={styles.buttonContainer}>
              <View style={styles.rowContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.editButton]}
                  onPress={() => setIsEditing(true)}
                >
                  <Text style={styles.buttonText}>Edit</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.button, styles.deleteButton]}
                  onPress={deleteQuote}
                >
                  <Text style={styles.buttonText}>Delete</Text>
                </TouchableOpacity>
              </View>
            </View>
          )
        )}
      </ScrollView>

      {/* Professional Quote Preview overlay - matching invoice-details.jsx */}
      {previewOpen && (
        <View style={styles.previewOverlay}>
          <View style={styles.previewCard}>
            <iframe
              srcDoc={`
                <!DOCTYPE html>
                <html>
                <head>
                  <meta charset="UTF-8">
                  <meta name="viewport" content="width=device-width, initial-scale=1.0">
                  <title>Quote Q-${quote.id || 'XXXX'} - ${quote.customerName || 'Customer'}</title>
                  <style>
                    @media print {
                      body { margin: 0; }
                      .action-buttons { display: none; }
                    }
                    
                    body {
                      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                      line-height: 1.3;
                      color: #333;
                      max-width: 700px;
                      margin: 0 auto;
                      padding: 0px 30px 60px 30px;
                      background: #fff;
                    }
                    
                    .header {
                      display: flex;
                      justify-content: space-between;
                      align-items: flex-start;
                      margin-bottom: 40px;
                      padding-bottom: 20px;
                      border-bottom: 3px solid #2563eb;
                      margin-top: 20px;
                    }
                    
                    .company-info {
                      flex: 1;
                    }
                    
                    .logo-container {
                      margin-bottom: 10px;
                    }
                    
                    .business-logo {
                      max-width: 150px;
                      max-height: 100px;
                      object-fit: contain;
                    }
                    
                    .logo-placeholder {
                      font-size: 12px;
                      color: #888;
                      font-style: italic;
                    }
                    
                    .company-name {
                      font-size: 18px;
                      font-weight: bold;
                      color: #2563eb;
                      margin-bottom: 10px;
                    }
                    
                    .company-details {
                      color: #666;
                      font-size: 14px;
                    }
                    
                    .quote-info {
                      text-align: right;
                      flex: 1;
                    }
                    
                    .quote-title {
                      font-size: 32px;
                      font-weight: bold;
                      color: #2563eb;
                      margin-bottom: 10px;
                    }
                    
                    .quote-details {
                      font-size: 14px;
                      color: #666;
                    }
                    
                    .customer-section {
                      margin-bottom: 30px;
                    }
                    
                    .section-title {
                      font-size: 18px;
                      font-weight: bold;
                      color: #374151;
                      margin-bottom: 15px;
                      padding-bottom: 5px;
                      border-bottom: 2px solid #e5e7eb;
                    }
                    
                    .customer-info {
                      background: #f8fafc;
                      padding: 20px;
                      border-radius: 8px;
                      border-left: 4px solid #2563eb;
                    }
                    
                    .customer-name {
                      font-size: 20px;
                      font-weight: bold;
                      color: #1f2937;
                      margin-bottom: 10px;
                    }
                    
                    .services-section {
                      margin-bottom: 30px;
                    }
                    
                    .service-item {
                      background: #fff;
                      border: 1px solid #e5e7eb;
                      border-radius: 8px;
                      padding: 25px;
                      margin-bottom: 20px;
                      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                    }
                    
                    .service-title {
                      font-size: 20px;
                      font-weight: bold;
                      color: #1f2937;
                      margin-bottom: 15px;
                    }
                    
                    .service-description {
                      color: #4b5563;
                      margin-bottom: 20px;
                      line-height: 1.7;
                      white-space: pre-wrap;
                    }
                    
                    .pricing-section {
                      background: #f8fafc;
                      border-radius: 8px;
                      padding: 25px;
                      margin-bottom: 30px;
                      border: 2px solid #2563eb;
                    }
                    
                    .price-row {
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                      padding: 15px 0;
                      border-bottom: 1px solid #e5e7eb;
                    }
                    
                    .price-row:last-child {
                      border-bottom: none;
                      font-size: 24px;
                      font-weight: bold;
                      color: #2563eb;
                      margin-top: 10px;
                      padding-top: 20px;
                      border-top: 2px solid #2563eb;
                    }
                    
                    .price-label {
                      font-size: 18px;
                      color: #374151;
                    }
                    
                    .price-amount {
                      font-size: 18px;
                      font-weight: bold;
                      color: #1f2937;
                    }
                    
                    .total-label {
                      font-size: 20px;
                      font-weight: bold;
                    }
                    
                    .total-amount {
                      font-size: 24px;
                      font-weight: bold;
                      color: #2563eb;
                    }
                    
                    .terms-section {
                      margin-top: 40px;
                      padding: 20px;
                      background: #fefce8;
                      border-radius: 8px;
                      border-left: 4px solid #eab308;
                    }
                    
                    .terms-title {
                      font-size: 16px;
                      font-weight: bold;
                      color: #92400e;
                      margin-bottom: 10px;
                    }
                    
                    .terms-text {
                      font-size: 14px;
                      color: #a16207;
                      line-height: 1.6;
                    }
                    
                    .footer {
                      margin-top: 50px;
                      padding-top: 20px;
                      border-top: 2px solid #e5e7eb;
                      text-align: center;
                      color: #6b7280;
                      font-size: 14px;
                    }
                    
                    .action-buttons {
                      position: fixed;
                      top: 20px;
                      right: 20px;
                      display: flex;
                      flex-direction: row;
                      gap: 10px;
                      z-index: 1000;
                      background: rgba(255,255,255,0.95);
                      padding: 10px;
                      border-radius: 10px;
                      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                    }
                    
                    .action-button {
                      background: #2563eb;
                      color: white;
                      border: none;
                      padding: 12px 20px;
                      border-radius: 8px;
                      font-size: 14px;
                      font-weight: bold;
                      cursor: pointer;
                      box-shadow: 0 2px 4px rgba(37, 99, 235, 0.3);
                      transition: all 0.2s;
                    }
                    
                    .action-button:hover {
                      background: #1d4ed8;
                      transform: translateY(-2px);
                      box-shadow: 0 4px 8px rgba(37, 99, 235, 0.4);
                    }
                    
                    .save-pdf-button {
                      background: #059669;
                    }
                    
                    .save-pdf-button:hover {
                      background: #047857;
                    }
                  </style>
                </head>
                <body>
                  <div class="header">
                    <div class="company-info">
                      <div class="logo-container">
                        ${businessLogoUrl 
                          ? `<img src="${businessLogoUrl}" alt="Business Logo" class="business-logo" />` 
                          : '<div class="logo-placeholder">Your business logo will appear here once uploaded by Owner/Admin in Settings.</div>'
                        }
                      </div>
                      <div class="company-name">${businessInfo?.businessName || 'Your Business Name'}</div>
                      <div class="company-details">
                        ${(() => {
                          const bInfo = businessInfo || {};
                          const addressParts = [];
                          
                          if (bInfo.firstName || bInfo.lastName) {
                            const fullName = [bInfo.firstName, bInfo.lastName].filter(Boolean).join(' ');
                            addressParts.push(fullName);
                          }
                          
                          if (bInfo.address) addressParts.push(bInfo.address);
                          if (bInfo.city && bInfo.state) {
                            const cityState = bInfo.zip ? `${bInfo.city}, ${bInfo.state} ${bInfo.zip}` : `${bInfo.city}, ${bInfo.state}`;
                            addressParts.push(cityState);
                          } else if (bInfo.city) {
                            addressParts.push(bInfo.city);
                          } else if (bInfo.state) {
                            addressParts.push(bInfo.state);
                          }
                          
                          if (bInfo.phone) {
                            addressParts.push(`Phone: ${bInfo.phone}`);
                          }
                          if (bInfo.email) {
                            addressParts.push(`Email: ${bInfo.email}`);
                          }
                          
                          // Add default contact info only if no business info available at all
                          if (addressParts.length === 0 && !bInfo.businessName) {
                            return `Contact info will be left blank until set by Owner/Admin in Settings.`;
                          }
                          
                          return addressParts.join('<br>') || 'Contact information not available';
                        })()}
                      </div>
                    </div>
                    <div class="quote-info">
                      <div class="quote-title">QUOTE</div>
                      <div class="quote-details">
                        <strong>Quote #:</strong> Q-${quote.id || 'XXXX'}<br>
                        <strong>Date:</strong> ${new Date().toLocaleDateString()}<br>
                        <strong>Valid Until:</strong> ${new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  <div class="customer-section">
                    <div class="section-title">Quote For:</div>
                    <div class="customer-info">
                      <div class="customer-name">${quote.customerName || 'Customer Name'}</div>
                      ${(() => {
                        const customer = customers.find(c => c.name === quote.customerName);
                        if (customer) {
                          return `
                            ${customer.address ? `<div>${customer.address}</div>` : ''}
                            ${customer.city && customer.state ? `<div>${customer.city}, ${customer.state} ${customer.zip || ''}</div>` : ''}
                            ${customer.phone ? `<div>Phone: ${customer.phone}</div>` : ''}
                            ${customer.email ? `<div>Email: ${customer.email}</div>` : ''}
                          `;
                        }
                        return '';
                      })()}
                    </div>
                  </div>

                  <div class="services-section">
                    <div class="section-title">Services</div>
                    <div class="service-item">
                      <div class="service-title">${quote.title || 'Service Title'}</div>
                      <div class="service-description">${quote.description || 'No details provided.'}</div>
                    </div>
                  </div>

                  <div class="pricing-section">
                    <div class="price-row">
                      <span class="price-label">Service Total:</span>
                      <span class="price-amount">$${parseFloat(quote.price || 0).toFixed(2)}</span>
                    </div>
                    <div class="price-row">
                      <span class="total-label">Total Amount:</span>
                      <span class="total-amount">$${parseFloat(quote.price || 0).toFixed(2)}</span>
                    </div>
                  </div>

                  <div class="terms-section">
                    <div class="terms-title">Quote Terms & Conditions</div>
                    <div class="terms-text">
                      • This quote is valid for 30 days from the date above<br>
                      • Prices are subject to change without notice<br>
                      • A 50% deposit may be required to begin work<br>
                      • Final pricing may vary based on actual work performed<br>
                      • Additional charges may apply for work outside the scope described above
                    </div>
                  </div>

                  <div class="footer">
                    <p>Thank you for considering our services. We look forward to working with you!</p>
                    <p><strong>Questions?</strong> Contact us${(() => {
                      const bInfo = businessInfo || {};
                      const contactParts = [];
                      
                      // Only add phone if it exists in business info
                      if (bInfo.phone) {
                        contactParts.push(`at ${bInfo.phone}`);
                      }
                      
                      // Only add email if it exists in business info  
                      if (bInfo.email) {
                        contactParts.push(`${contactParts.length > 0 ? 'or ' : 'at '}${bInfo.email}`);
                      }
                      
                      // Fallback to defaults if no contact info available
                      if (contactParts.length === 0) {
                        contactParts.push('at company phone or email.');
                      }
                      
                      return contactParts.length > 0 ? ' ' + contactParts.join(' ') : '';
                    })()}</p>
                  </div>

                </body>
                </html>
              `}
              style={{
                width: '100%',
                height: '80vh',
                border: 'none',
                borderRadius: '8px'
              }}
              title="Quote Preview"
            />
            
            <View style={styles.modalButtons}>
              <TouchableOpacity 
                style={[styles.modalButton, styles.emailModalButton, sendingEmail && { opacity: 0.7 }]}
                onPress={async () => {
                  setPreviewOpen(false);
                  emailQuotePDF();
                }}
                disabled={sendingEmail}
              >
                <Text style={styles.previewActionButtonText}>
                  {sendingEmail ? 'Sending…' : '📧 One-Touch Email'}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity 
                style={[styles.modalButton, styles.savePdfButton]} 
                onPress={generateAndSharePDF}
              >
                <Text style={styles.previewActionButtonText}>💾 Save/Share PDF</Text>
              </TouchableOpacity>
            </View>

            <TouchableOpacity 
              style={styles.closePreviewLink}
              onPress={() => setPreviewOpen(false)}
            >
              <Text style={styles.closePreviewButtonText}>Close Preview</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Custom Modal */}
      {customModal.open && (
        <View style={styles.previewOverlay}>
          <View style={styles.previewCard}>
            <Text style={styles.previewTitle}>{customModal.title}</Text>
            <Text>{customModal.message}</Text>
            <View style={styles.buttonGroup}>
              {customModal.buttons.map((button, index) => (
                <TouchableOpacity
                  key={index}
                  style={[
                    styles.modalActionButton,
                    button.style === 'primary' && { backgroundColor: 'blue' },
                    button.style === 'secondary' && { backgroundColor: 'gray' },
                    button.style === 'danger' && { backgroundColor: 'red' },
                    button.style === 'default' && { backgroundColor: '#f0f0f0' },
                  ]}
                  onPress={button.onPress}
                >
                  <Text style={[
                    styles.modalActionButtonText,
                    button.style === 'default' && { color: 'black' },
                    button.style !== 'default' && { color: 'white' }
                  ]}>
                    {button.text}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
      )}
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 20,
    paddingBottom: 100,
    backgroundColor: '#f0f8ff',
  },
  label: {
    fontWeight: 'bold',
    marginTop: 15,
    fontSize: 20,
    color: '#333',
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    paddingBottom: 5,
  },
  value: {
    fontSize: 16,
    marginTop: 5,
    color: '#555',
    padding: 0,
    backgroundColor: 'transparent',
    borderRadius: 0,
    shadowColor: 'transparent',
    elevation: 0,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ccc',
    padding: 8,
    borderRadius: 4,
    marginTop: 5,
    backgroundColor: '#fff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 1,
  },
  buttonContainer: { marginTop: 30 },
  rowContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
  },
  button: {
    flex: 1,
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
    marginHorizontal: 20,
  },
  editButton: { backgroundColor: 'blue' },
  deleteButton: {
    backgroundColor: '#FF3B30',
    padding: 8,
    borderRadius: 5,
    alignItems: 'center',
    flex: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 3,
    elevation: 2,
  },
  buttonText: { color: 'white', fontWeight: 'bold', fontSize: 14 },
  saveButton: {
    backgroundColor: '#34C759',
  },
  saveButtonText: { color: '#fff', fontSize: 14, fontWeight: 'bold' },
  cancelButton: {
    backgroundColor: '#FF3B30',
  },
  sentContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 10,
  },
  sentStatusContainer: {
    flex: 1,
  },
  dateSentText: {
    fontSize: 14,
    color: '#888',
    marginTop: 5,
  },
  sentButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    flex: 1,
  },
  actionButton: {
    padding: 8,
    borderRadius: 5,
    alignItems: 'center',
    marginHorizontal: 5,
  },
  resendButton: {
    backgroundColor: '#007AFF',
  },
  previewButton: {
    backgroundColor: '#34C759',
  },
  emailButton: {
    backgroundColor: '#007AFF',
  },
  actionButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: 'bold',
  },
  previewOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  previewCard: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 10,
    width: '60%',
    maxWidth: '700px',
    height: '95%',
  },
  previewTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  bold: {
    fontWeight: 'bold',
  },
  buttonGroup: {
    marginTop: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  previewActionButton: {
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
    flex: 1,
    marginHorizontal: 5,
  },
  pdfButton: {
    backgroundColor: '#007AFF',
  },
  emailFromPreviewButton: {
    backgroundColor: '#34C759',
  },
  closeButton: {
    backgroundColor: '#FF3B30',
  },
  previewActionButtonText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 14,
  },
  modalActionButton: {
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
    flex: 1,
    marginHorizontal: 5,
  },
  modalActionButtonText: {
    fontWeight: 'bold',
    fontSize: 14,
  },
  customerDetailsLink: {
    color: '#007AFF',
    fontWeight: 'bold',
    fontSize: 14,
    textDecorationLine: 'underline',
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 15,
    paddingBottom: 10,
    gap: 10,
  },
  modalButton: {
    flex: 1,
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
  },
  emailModalButton: {
    backgroundColor: 'green',
  },
  savePdfButton: {
    backgroundColor: 'blue',
  },
  closePreviewLink: {
    alignItems: 'center',
    paddingVertical: 15,
    paddingBottom: 20,
  },
  closePreviewButtonText: {
    color: '#FF3B30',
    fontWeight: 'bold',
    fontSize: 16,
    textDecorationLine: 'underline',
  },
});
